import { appToday } from "../../lib/devDate";
import type { StatisticsFilters, StatisticsPreset } from "./types";

export function isoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function defaultStatisticsFilters(today = appToday()): StatisticsFilters {
  return {
    dateFrom: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    dateTo: isoDate(today),
    bucket: "day",
    type: "",
    categoryId: "",
    userId: "",
    accountId: "",
    tagIds: [],
    merchant: "",
    compare: "previous",
  };
}

export function statisticsPresetFilters(
  preset: StatisticsPreset,
  current: StatisticsFilters,
  today = appToday(),
): StatisticsFilters {
  if (preset === "month") {
    return { ...current, dateFrom: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), dateTo: isoDate(today), bucket: "day" };
  }
  if (preset === "lastMonth") {
    return {
      ...current,
      dateFrom: isoDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      dateTo: isoDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      bucket: "day",
    };
  }
  if (preset === "quarter") {
    return { ...current, dateFrom: isoDate(new Date(today.getFullYear(), today.getMonth() - 2, 1)), dateTo: isoDate(today), bucket: "week" };
  }
  return { ...current, dateFrom: isoDate(new Date(today.getFullYear(), 0, 1)), dateTo: isoDate(today), bucket: "month" };
}

export function recommendedStatisticsBucket(dateFrom: string, dateTo: string): StatisticsFilters["bucket"] {
  const days = (new Date(dateTo).getTime() - new Date(dateFrom).getTime()) / 86400000;
  if (!Number.isFinite(days) || days <= 45) return "day";
  if (days <= 130) return "week";
  return "month";
}

export function currentStatisticsMonth(today = appToday()) {
  return isoDate(new Date(today.getFullYear(), today.getMonth(), 1)).slice(0, 7);
}

export function normalizeStatisticsMonth(value: string | null | undefined, today = appToday()) {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return currentStatisticsMonth(today);
  const [year, month] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, 1);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1) return currentStatisticsMonth(today);
  return value > currentStatisticsMonth(today) ? currentStatisticsMonth(today) : value;
}

export function statisticsMonthFilters(month: string, current: StatisticsFilters, today = appToday()): StatisticsFilters {
  const normalized = normalizeStatisticsMonth(month, today);
  const [year, monthNumber] = normalized.split("-").map(Number);
  const isCurrent = normalized === currentStatisticsMonth(today);
  return {
    ...current,
    dateFrom: isoDate(new Date(year, monthNumber - 1, 1)),
    dateTo: isCurrent ? isoDate(today) : isoDate(new Date(year, monthNumber, 0)),
    bucket: "day",
  };
}

export function shiftStatisticsMonth(month: string, offset: number, today = appToday()) {
  const normalized = normalizeStatisticsMonth(month, today);
  const [year, monthNumber] = normalized.split("-").map(Number);
  const shifted = isoDate(new Date(year, monthNumber - 1 + offset, 1)).slice(0, 7);
  return normalizeStatisticsMonth(shifted, today);
}

export function statisticsMonthLabel(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(year, monthNumber - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
